import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { TopBar } from '@/components/TopBar';
import { BrandingPanel } from '@/components/BrandingPanel';
import { TeamPanel } from '@/components/TeamPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { KeyRound, Loader2, User, Mail, CheckCircle2, Palette, Users } from 'lucide-react';

function AccountPanel() {
  const { user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setError('New password and confirmation do not match.'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated', { description: 'Use your new password next time you sign in.' });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="cx-glass rounded-2xl p-5 flex items-center gap-4" data-testid="settings-account-card">
        <div className="h-11 w-11 rounded-xl bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)] flex items-center justify-center">
          <User className="h-5 w-5 text-[color:var(--cx-primary)]" />
        </div>
        <div className="min-w-0">
          <div className="font-display text-[color:var(--cx-fg)]">{user?.name || 'User'}</div>
          <div className="flex items-center gap-1.5 text-sm text-[color:var(--cx-fg-muted)]"><Mail className="h-3.5 w-3.5" />{user?.email}</div>
        </div>
        <span className="ml-auto inline-flex items-center rounded-full border border-[color:var(--cx-border)] px-2.5 py-1 text-[10px] uppercase tracking-wide text-[color:var(--cx-fg-muted)]">
          {user?.permission === 'manage' ? 'Manage' : 'View only'}
        </span>
      </div>

      <div className="cx-glass rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[color:var(--cx-border)] flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-[color:var(--cx-primary)]" />
          <h2 className="font-display text-[color:var(--cx-fg)]">Change password</h2>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-[color:var(--cx-fg-muted)]">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required data-testid="settings-current-password" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[color:var(--cx-fg-muted)]">New password</Label>
              <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required data-testid="settings-new-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[color:var(--cx-fg-muted)]">Confirm new password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required data-testid="settings-confirm-password" />
            </div>
          </div>
          <p className="text-xs text-[color:var(--cx-muted)]">Use at least 8 characters. You’ll stay signed in on this device.</p>
          {error && <p className="text-sm text-[color:var(--destructive)]" data-testid="settings-error">{error}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} data-testid="settings-save-button">
              {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Updating…</>) : (<><CheckCircle2 className="h-4 w-4 mr-2" />Update password</>)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const canManage = user?.permission === 'manage';
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const initial = (requested === 'team' || requested === 'branding') && canManage ? requested : 'account';
  const [tab, setTab] = useState(initial);

  const onTabChange = (v) => {
    setTab(v);
    setParams(v === 'account' ? {} : { tab: v }, { replace: true });
  };

  return (
    <div className="cx-page pb-16">
      <TopBar />
      <main className="mx-auto max-w-[860px] px-4 sm:px-6 lg:px-8">
        <section className="pt-10 pb-6">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <p className="font-mono text-xs text-[color:var(--cx-primary)] tracking-wide">SETTINGS</p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-[color:var(--cx-fg)]">Portal settings</h1>
            <p className="mt-2 text-[color:var(--cx-fg-muted)]">Manage your account{canManage ? ', branding and team access' : ''}.</p>
          </motion.div>
        </section>

        <Tabs value={tab} onValueChange={onTabChange}>
          <TabsList className="bg-[color:var(--cx-surface-solid)] border border-[color:var(--cx-border)]">
            <TabsTrigger value="account" data-testid="settings-tab-account"><KeyRound className="h-4 w-4 mr-1.5" />Account</TabsTrigger>
            {canManage && <TabsTrigger value="branding" data-testid="settings-tab-branding"><Palette className="h-4 w-4 mr-1.5" />Branding</TabsTrigger>}
            {canManage && <TabsTrigger value="team" data-testid="settings-tab-team"><Users className="h-4 w-4 mr-1.5" />Team</TabsTrigger>}
          </TabsList>

          <TabsContent value="account" className="mt-5"><AccountPanel /></TabsContent>
          {canManage && <TabsContent value="branding" className="mt-5"><BrandingPanel /></TabsContent>}
          {canManage && <TabsContent value="team" className="mt-5"><TeamPanel /></TabsContent>}
        </Tabs>
      </main>
    </div>
  );
}
